import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { Lumber, LumberWithStockLengthAndPrice } from '@paragon/contract-library';
import { CostWeights, OptimizerMissingLumberPrice, OptimizerMissingPlatePrice } from '@paragon/data-connections';

/**
 * Settings for the single-component Optimization panel. Separate from the Analyze popover's optimization flags on
 * `applicationSlice`.
 */

export interface OptimizationCostTerm {
  readonly enabled: boolean;
  readonly value: number;
}

export interface OptimizationLaborSettings {
  readonly perCut: OptimizationCostTerm;
  readonly perMember: OptimizationCostTerm;
  readonly perUniqueMember: OptimizationCostTerm;
  readonly perJoint: OptimizationCostTerm;
  readonly perSplice: OptimizationCostTerm;
  readonly perBoardFoot: OptimizationCostTerm;
  readonly perPlate: OptimizationCostTerm;
  readonly perUniquePlate: OptimizationCostTerm;
  readonly twoPlyLabor: OptimizationCostTerm;
  readonly threePlyLabor: OptimizationCostTerm;
}

export type OptimizationLaborTerm = keyof OptimizationLaborSettings;

export interface OptimizerLevers {
  readonly resizeChords: boolean;
  readonly plating: boolean;
  readonly exhaustivePlating: boolean;
  readonly varyPlyCount: boolean;
  readonly varySplicing: boolean;
}

export type OptimizerLever = keyof OptimizerLevers;

export interface MissingLumberPriceRow {
  readonly lumber: Lumber;
  readonly stockLengthInInches: number;
  readonly defaultPricePerThousandBoardFeet: number | null;
  readonly price: number | null;
}

export interface MissingPlatePriceRow {
  readonly plateType: string;
  readonly defaultPricePerPound: number;
  readonly price: number | null;
}

export type MissingOptimizerPricesState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading'; readonly componentDesignGuid: string }
  | {
      readonly status: 'loaded';
      readonly componentDesignGuid: string;
      readonly lumberRows: readonly MissingLumberPriceRow[];
      readonly plateRows: readonly MissingPlatePriceRow[];
    }
  | { readonly status: 'error'; readonly componentDesignGuid: string };

export interface OptimizationCostSettings {
  readonly labor: OptimizationLaborSettings;
  readonly levers: OptimizerLevers;
  /** Highest ply count the optimizer may try when the `varyPlyCount` lever is on. */
  readonly maxPlies: number;
  /** Price per thousand board feet */
  readonly bracingPrice: OptimizationCostTerm;
  readonly missingOptimizerPrices: MissingOptimizerPricesState;
}

const initialState: OptimizationCostSettings = {
  labor: {
    perCut: { enabled: false, value: 0 },
    perMember: { enabled: false, value: 0 },
    perUniqueMember: { enabled: false, value: 0 },
    perJoint: { enabled: false, value: 0 },
    perSplice: { enabled: false, value: 0 },
    perBoardFoot: { enabled: false, value: 0 },
    perPlate: { enabled: false, value: 0 },
    perUniquePlate: { enabled: false, value: 0 },
    twoPlyLabor: { enabled: false, value: 0 },
    threePlyLabor: { enabled: false, value: 0 },
  },
  levers: {
    resizeChords: false,
    plating: true,
    exhaustivePlating: false,
    varyPlyCount: false,
    varySplicing: true,
  },
  maxPlies: 3,
  bracingPrice: { enabled: false, value: 0 },
  missingOptimizerPrices: { status: 'idle' },
};

const optimizationSettingsSlice = createSlice({
  name: 'optimizationSettings',
  initialState,
  reducers: {
    setLaborTermEnabled: (state, action: PayloadAction<{ term: OptimizationLaborTerm; enabled: boolean }>) => {
      state.labor[action.payload.term].enabled = action.payload.enabled;
    },
    setLaborTermValue: (state, action: PayloadAction<{ term: OptimizationLaborTerm; value: number }>) => {
      state.labor[action.payload.term].value = action.payload.value;
    },
    setLever: (state, action: PayloadAction<{ lever: OptimizerLever; value: boolean }>) => {
      state.levers[action.payload.lever] = action.payload.value;
    },
    setMaxPlies: (state, action: PayloadAction<number>) => {
      state.maxPlies = action.payload;
    },
    setBracingPrice: (state, action: PayloadAction<OptimizationCostTerm>) => {
      state.bracingPrice = action.payload;
    },
    missingOptimizerPricesRequested: (state, action: PayloadAction<{ componentDesignGuid: string }>) => {
      state.missingOptimizerPrices = { status: 'loading', componentDesignGuid: action.payload.componentDesignGuid };
    },
    missingOptimizerPricesLoaded: (
      state,
      action: PayloadAction<{
        componentDesignGuid: string;
        lumber: readonly OptimizerMissingLumberPrice[];
        plates: readonly OptimizerMissingPlatePrice[];
      }>,
    ) => {
      state.missingOptimizerPrices = castDraft({
        status: 'loaded' as const,
        componentDesignGuid: action.payload.componentDesignGuid,
        lumberRows: action.payload.lumber.map((missing) => ({
          ...missing,
          price: missing.defaultPricePerThousandBoardFeet,
        })),
        plateRows: action.payload.plates.map((missing) => ({ ...missing, price: missing.defaultPricePerPound })),
      });
    },
    missingOptimizerPricesFailed: (state, action: PayloadAction<{ componentDesignGuid: string }>) => {
      const { missingOptimizerPrices } = state;
      if (
        missingOptimizerPrices.status === 'loading' &&
        missingOptimizerPrices.componentDesignGuid === action.payload.componentDesignGuid
      ) {
        state.missingOptimizerPrices = { status: 'error', componentDesignGuid: action.payload.componentDesignGuid };
      }
    },
    setMissingLumberPrice: (state, action: PayloadAction<{ index: number; price: number }>) => {
      if (state.missingOptimizerPrices.status !== 'loaded') return;
      const row = state.missingOptimizerPrices.lumberRows[action.payload.index];
      if (row) row.price = action.payload.price;
    },
    resetMissingLumberPricesToDefaults: (state) => {
      if (state.missingOptimizerPrices.status !== 'loaded') return;
      for (const row of state.missingOptimizerPrices.lumberRows) {
        row.price = row.defaultPricePerThousandBoardFeet;
      }
    },
    setMissingPlatePrice: (state, action: PayloadAction<{ index: number; price: number }>) => {
      if (state.missingOptimizerPrices.status !== 'loaded') return;
      const row = state.missingOptimizerPrices.plateRows[action.payload.index];
      if (row) row.price = action.payload.price;
    },
    resetMissingPlatePricesToDefaults: (state) => {
      if (state.missingOptimizerPrices.status !== 'loaded') return;
      for (const row of state.missingOptimizerPrices.plateRows) {
        row.price = row.defaultPricePerPound;
      }
    },
  },
});

export const {
  setLaborTermEnabled,
  setLaborTermValue,
  setLever,
  setMaxPlies,
  setBracingPrice,
  missingOptimizerPricesRequested,
  missingOptimizerPricesLoaded,
  missingOptimizerPricesFailed,
  setMissingLumberPrice,
  resetMissingLumberPricesToDefaults,
  setMissingPlatePrice,
  resetMissingPlatePricesToDefaults,
} = optimizationSettingsSlice.actions;
export const optimizationSettings = optimizationSettingsSlice.reducer;

export function mapCostFactorsToCostWeights(
  labor: OptimizationLaborSettings,
  bracingPrice: OptimizationCostTerm,
): CostWeights {
  const weightForTerm = (term: OptimizationCostTerm) => (term.enabled ? term.value : 0);
  return {
    perMember: weightForTerm(labor.perMember),
    perUniqueMember: weightForTerm(labor.perUniqueMember),
    perCut: weightForTerm(labor.perCut),
    perJoint: weightForTerm(labor.perJoint),
    perSplice: weightForTerm(labor.perSplice),
    perBoardFoot: weightForTerm(labor.perBoardFoot),
    perPlate: weightForTerm(labor.perPlate),
    twoPlyLabor: weightForTerm(labor.twoPlyLabor),
    threePlyLabor: weightForTerm(labor.threePlyLabor),
    bracing: weightForTerm(bracingPrice),
  };
}

export function mapMissingLumberPricesToUserSpecified(
  rows: readonly MissingLumberPriceRow[],
): LumberWithStockLengthAndPrice[] {
  return rows
    .filter((row): row is MissingLumberPriceRow & { price: number } => row.price != null)
    .map((row) => ({ lumber: row.lumber, stockLengthInInches: row.stockLengthInInches, price: row.price }));
}

export function mapMissingPlatePricesToUserSpecified(rows: readonly MissingPlatePriceRow[]): {
  readonly [plateType: string]: number;
} {
  return Object.fromEntries(
    rows
      .filter((row): row is MissingPlatePriceRow & { price: number } => row.price != null)
      .map((row) => [row.plateType, row.price]),
  );
}
